import $ from 'jquery';

// eslint-disable-next-line import/no-unresolved
import './frontend.css';

// Store GFPCL instance in window, so it can be accessed immediately even if Page Conditional Logic inits early.
window.gform.addAction(
	'gform_frontend_pages_evaluated',
	(pages: any, formId: number, self: any) => {
		(window as any)[`gspc_gfpcl_${formId}`] = self;
	}
);

interface GSProductConfiguratorArgs {
	formId: number;
	isAjax: boolean;
	productBasePriceFieldId: number;
	productType: string;
	canEditForms: boolean;
}

interface PageState {
	activePageCount: number;
	currentPage: number;
	currentStep: number;
	firstPage: number;
	inactivePages: number[];
	lastPage: number;
	nextPage: number;
	previousPage: number;
	totalPageCount: number;
}

interface GSProductConfigurator extends GSProductConfiguratorArgs {}

class GSProductConfigurator implements GSProductConfigurator {
	public formId!: number;
	public currentPage?: number;
	public gfPageConditionalLogic?: any;
	public pageClIsOnLastVisiblePage?: boolean;

	/** Available in GF 2.9 and newer. */
	public pageState?: PageState;

	/**
	 * Form element attributes. Store these as during AJAX form submissions, the form will be replaced and attributes
	 * will be lost.
	 */
	public formAttributes?: NamedNodeMap;

	/**
	 * Store quantity div in case it's detached with AJAX forms.
	 */
	public _$quantityDiv?: JQuery<HTMLElement>;

	public quantityDivSelector = 'div.quantity';

	/**
	 * Store Add to Cart button in case it's detached with AJAX forms.
	 */
	public _$addToCart?: JQuery<HTMLElement>;

	public addToCartSelector =
		'button[name="add-to-cart"], button[class*="add_to_cart_button"]';

	/**
	 * Store variations selector in case it's detached with AJAX forms.
	 */
	public _$variations?: JQuery<HTMLElement>;

	public variationsSelector = 'table.variations';

	/**
	 * Use a getter as the form element can become detached with AJAX forms.
	 */
	get $formEl(): JQuery<HTMLElement> {
		return $(`#gform_${this.formId}`);
	}

	/**
	 * Use a getter as the form element can become detached with AJAX forms.
	 */
	get $currentPage(): JQuery<HTMLElement> {
		return $(`#gform_page_${this.formId}_${this.currentPage}`);
	}

	/**
	 * Use a getter as the form element can become detached with AJAX forms.
	 */
	get $formFooter(): JQuery<HTMLElement> {
		if (this.$currentPage.length) {
			return this.$currentPage.find('.gform_page_footer');
		}

		return this.$formEl.find('.gform_footer');
	}

	/**
	 * Property and getter to store the product ID.
	 */
	private _productId?: number;

	get productId(): number | null {
		// Use cached value if it exists.
		if (this._productId) {
			return this._productId;
		}

		// Try to get it from add to cart button first.
		const $addToCart = this.$formEl.find(this.addToCartSelector);

		if ($addToCart.val()) {
			this._productId = parseInt($addToCart.val() as string, 10);

			return this._productId;
		}

		// Fall back to the hidden input for the add-to-cart.
		const $input = this.$formEl.find('input[name="add-to-cart"]');

		if ($input.length) {
			this._productId = parseInt($input.val() as string, 10);

			return this._productId;
		}

		// Fall back to the product ID in the GSPC product ID input.
		const $productInput = this.$formEl.find(
			'input[name="input_gspc_product_id"]'
		);

		if ($productInput.length) {
			this._productId = parseInt($productInput.val() as string, 10);

			return this._productId;
		}

		return null;
	}

	/**
	 * Property and getter to store the variation ID. Mostly needed for AJAX forms.
	 */
	private _variationId?: number;

	get variationId(): number | null {
		// Use cached value if it exists.
		if (this._variationId) {
			return this._variationId;
		}

		// Fall back to the hidden input for the variation_id.
		const $input = this.$formEl.find('input[name="variation_id"]');

		if ($input.length) {
			this._variationId = parseInt($input.val() as string, 10);

			return this._variationId;
		}

		// Fall back to the variation ID in the GSPC variation ID input.
		const $gcgsInput = this.$formEl.find(
			'input[name="input_gspc_variation_id"]'
		);

		if ($gcgsInput.length) {
			this._variationId = parseInt($gcgsInput.val() as string, 10);

			return this._variationId;
		}

		return null;
	}

	constructor(args: GSProductConfiguratorArgs) {
		// Do not re-create the entire object if it already exists.
		const existingInstance = (window as any)?.[
			`gs_product_configurator_${args.formId}`
		];

		if (existingInstance) {
			this.gfPageConditionalLogic = undefined;
			existingInstance.bindFormEventsGFPL();
			existingInstance.bindFormEventsGPPT();

			return;
		}

		Object.assign(this, args);

		if (this.canEditForms) {
			this.showAdminAlerts();
		}

		// Bind events for both non-AJAX and AJAX forms.
		this.bindGlobalEvents();
		this.bindProductImageEvents();
		this.bindWCBookingsEvents();
		this.bindAddToCartClick();

		// Cache quantity div and Add to Cart button.
		if (this.isAjax) {
			this.cacheFormAttributes();
			this.cacheQuantityDiv();
			this.cacheAddToCart();
			this.cacheVariations();
		}

		// Set gfPageConditionalLogic from window if it exists. In some cases Page CL is inited before this init script.
		this.gfPageConditionalLogic = (window as any)?.[
			`gspc_gfpcl_${this.formId}`
		];

		// Do not do anything more if the form doesn't have pages.
		if (this.$formEl.find('.gform_page').length <= 1) {
			return;
		}

		if (this.isAjax) {
			this.bindEventsAJAX();
			this.bindFormEventsGPPT();
		} else {
			// Set current page for non-AJAX forms.
			// @ts-ignore
			this.setCurrentPage(
				parseInt(
					$(
						`#gform_source_page_number_${this.formId}`
					).val() as string,
					10
				)
			);
		}

		this.bindFormEventsGFPL();
		this.updateUI();
	}

	/**
	 * Displays various errors useful to admins.
	 */
	public showAdminAlerts() {
		/*
		 * If the form is detected as a multi-page form and the product type is "booking", show an alert. This is because
		 * when navigating a multi-page form, the selected booking time is lost.
		 */
		if (
			this.productType === 'booking' &&
			this.$formEl.find('.gform_page').length > 1
		) {
			// eslint-disable-next-line no-console
			console.error(
				`GS Product Configurator: The form with ID ${this.formId} is a multi-page form and the product type is "booking". When navigating a multi-page form, the selected booking time is lost.`
			);

			// eslint-disable-next-line no-alert
			window.alert(
				`(Only visible to admins)\n\nGS Product Configurator: The form with ID ${this.formId} is a multi-page form and the product type is "booking". When navigating a multi-page form, the selected booking time is lost.`
			);
		}
	}

	/**
	 * Bind events for all form types (AJAX and non-AJAX)
	 */
	public bindGlobalEvents() {
		window.gform.addFilter(
			'gform_spinner_target_elem',
			($target: JQuery<HTMLElement>, formId: number) => {
				if (formId !== this.formId) {
					return $target;
				}

				const $addToCart = this.$formEl.find(
					'[name="add-to-cart"]:visible'
				);

				if ($addToCart.length) {
					return $addToCart;
				}

				return $target;
			},
			50,
			'gspc_set_spinner_target'
		);

		window.gform.addFilter(
			'gform_abort_conditional_logic_do_action',
			(
				shouldAbort,
				action,
				targetId,
				animation,
				defaultValues,
				isInit,
				formId
			) => {
				if (
					parseInt(formId) === this.formId &&
					targetId === `#gform_submit_button_${this.formId}`
				) {
					// We prevent the conditional logic callback from executing as it will trigger the `gform_post_conditional_logic`
					// which is causing some weird race condition when the dependent field is a calculated field. See Ticket #70013
					window.do_callback = null;
				}

				return shouldAbort;
			}
		);

		window.gform.addAction(
			'gform_post_conditional_logic_field_action',
			(formId, action, targetId, defaultValues, isInit) => {
				if (parseInt(formId) !== this.formId) {
					return;
				}

				if (targetId !== `#gform_submit_button_${this.formId}`) {
					return;
				}

				if (action === 'show') {
					this.$formEl.removeClass(
						'gspc__form--submit-conditionally-hidden'
					);
				} else if (action === 'hide') {
					this.$formEl.addClass(
						'gspc__form--submit-conditionally-hidden'
					);
				}
			}
		);

		this.bindEventsAJAX();

		window.gform.addFilter(
			'gform_is_value_match',
			this.isValidMatchVariation
		);
		window.gform.addFilter(
			'gform_is_value_match',
			this.isValidMatchQuantity
		);

		// Trigger conditional logic when a variation changes.
		this.$formEl.on('change', 'select[data-attribute_name]', (event) => {
			const attributeName = $(event?.currentTarget)
				.data('attribute_name')
				.replace('attribute_', '');
			const dependents = this.getConditionalLogicDependents(
				`gspc_product_variation_${attributeName}`
			);

			window?.gf_apply_rules?.(this.formId, dependents);
		});

		// Trigger conditional logic when the WooCommerce quantity changes.
		this.getQuantityInput().on('change keyup', (event) => {
			const dependents =
				this.getConditionalLogicDependents('gspc_quantity');

			window?.gf_apply_rules?.(this.formId, dependents);
		});

		// Update the base product price when a variation changes.
		this.$formEl.on('found_variation', this.onVariationChange);

		// Handle resetting variations (conditional logic, etc)
		this.$formEl.on('reset_data', (event) => {
			let dependents: string[] = [];

			this.$formEl.find('select[data-attribute_name]').each((i, el) => {
				const attributeName = $(el)
					.data('attribute_name')
					.replace('attribute_', '');

				dependents = [
					...dependents,
					...this.getConditionalLogicDependents(
						`gspc_product_variation_${attributeName}`
					),
				];
			});

			window?.gf_apply_rules?.(this.formId, dependents);

			// Update input_gspc_variation_id hidden input to be used primarily with GPPA.
			this.$formEl.find(`[name="input_gspc_variation_id"]`).val('');

			// Update the price
			this.updateProductPrice(
				this.$formEl.find('[name="gspc_product_price"]').val() as number
			);
		});

		// Add the Add to Cart button inside the form to GPPA's submit button list, so it can be enabled/disabled.
		window.gform.addFilter(
			'gppa_submit_button',
			function ($submitButton: JQuery, $form: JQuery) {
				return $submitButton.add(
					$form.find('button[name="add-to-cart"]')
				);
			}
		);
	}

	/**
	 * Handler when variation changes.
	 */
	public onVariationChange = (event: JQuery.Event, variation: any) => {
		this.updateProductPrice(variation.display_price);

		// Update input_gspc_variation_id hidden input to be used primarily with GPPA.
		this.$formEl
			.find(`[name="input_gspc_variation_id"]`)
			.val(variation.variation_id);

		this._variationId = variation.variation_id;
	};

	/**
	 * Update WooCommerce product price. Used for changing the WooCommerce product price that's injected into the form.
	 */
	public updateProductPrice(price: number) {
		const $basePriceInput = this.$formEl.find(
			`#ginput_base_price_${this.formId}_${this.productBasePriceFieldId}`
		);

		if (!$basePriceInput.length) {
			return;
		}

		const currency = new window.Currency(
			window.gf_global.gf_currency_config
		);

		const value = currency.toMoney(price, true);
		if (value !== $basePriceInput.val()) {
			$basePriceInput.val(value).trigger('change');
		}

		window.gformCalculateTotalPrice(this.formId);
	}

	/**
	 * Find conditional logic dependents that either match a fieldId literally or using a callback.
	 */
	public getConditionalLogicDependents(
		fieldId: string | ((fieldId: number | string) => boolean)
	): string[] {
		const cl = window?.gf_form_conditional_logic?.[this.formId];

		if (!cl) {
			return [];
		}

		const dependents = [];

		for (const [logicFieldId, logic] of Object.entries(cl.logic)) {
			// @ts-ignore
			if (!logic.field && !logic.nextButton) {
				continue;
			}

			// @ts-ignore
			const ruleSets = [
				// @ts-ignore
				logic.field?.rules,
				// @ts-ignore
				logic.nextButton?.rules,
			].filter(Boolean);

			for (const rules of ruleSets) {
				// @ts-ignore
				for (const rule of rules) {
					if (typeof fieldId === 'function') {
						if (!fieldId(rule.fieldId)) {
							continue;
						}
					} else if (rule.fieldId !== fieldId) {
						continue;
					}

					dependents.push(logicFieldId);
				}
			}
		}

		return dependents;
	}

	/**
	 * Bind callbacks on a variety of events such as the page changing.
	 */
	public bindEventsAJAX() {
		$(document).on(
			'gform_page_loaded gform_post_render',
			(event, formId: number, currentPage: string) => {
				// eslint-disable-next-line eqeqeq
				if (formId != this.formId) {
					return;
				}

				this.setCurrentPage(parseInt(currentPage));
				this.updateUI();
			}
		);
	}

	/**
	 * Bind callbacks on the form el for Page Conditional Logic
	 */
	public bindFormEventsGFPL() {
		window.gform.addAction(
			'gform_frontend_pages_evaluated',
			(pages: any, formId: number, self: any) => {
				if (formId !== this.formId) {
					return;
				}

				if (typeof this.gfPageConditionalLogic === 'undefined') {
					this.gfPageConditionalLogic = self;
				}

				this.updateUI();
			}
		);

		/*
		 * GF 2.9 compatibility.
		 *
		 * This logic watches the state and updates the page count and form UI.
		 */
		(window?.gform as any)?.state?.watch(
			this.formId,
			['pagination/pages'],
			(formId: number, key: string, data: any) => {
				this.setPageState(data[key].value);
				this.updateUI();
			}
		);
	}

	/**
	 * Helper method to swap out the product image.
	 */
	public swapProductImageUsingChoice(choice: any) {
		// If this field is hidden, don't do anything.
		if ($(choice).closest('.gfield').is(':hidden')) {
			return;
		}

		const image = $(choice).attr('data-gspc-product-image') as string;

		this.swapProductImage(image, $(choice).closest('.product'));
	}

	/**
	 * Helper method to swap out the product image.
	 */
	public swapProductImage(image: string, $product: JQuery) {
		if (!image) {
			return;
		}

		const $productGallery = $product.find('.images');
		const $galleryNav = $product.find('.flex-control-nav');
		const $galleryImg = $galleryNav.find('li:eq(0) img');

		const $productImgWrap = $productGallery
			.find(
				'.woocommerce-product-gallery__image, .woocommerce-product-gallery__image--placeholder'
			)
			.eq(0);

		const $productImg = $productImgWrap.find('.wp-post-image');
		const $productLink = $productImgWrap.find('a').eq(0);

		// Store the original image in a data attribute so we can swap it back later.
		if (!$productImg.data('original-image')) {
			$productImg.data(
				'original-image',
				$productImg.attr('src') as string
			);
		}

		const tempImg = new Image();
		tempImg.src = image;

		tempImg.onload = function () {
			const width = tempImg.width;
			const height = tempImg.height;

			$productImg.removeAttr('height');
			$productImg.removeAttr('width');
			$productImg.attr('srcset', image);
			$productImg.removeAttr('sizes');
			$productImg.removeAttr('title');
			$productImg.removeAttr('data-caption');
			$productImg.removeAttr('alt');
			$productImg.attr('data-src', image);
			$productImg.attr('data-large_image', image);
			$productImg.attr('data-large_image_width', width);
			$productImg.attr('data-large_image_height', height);
			$productImgWrap.attr('data-thumb', image);
			$galleryImg.attr('src', image);
			$productLink.attr('href', image);

			window.setTimeout(function () {
				$(window).trigger('resize');
				$productGallery.trigger('woocommerce_gallery_init_zoom');
			}, 20);
		};
	}

	/**
	 * Bind events to handle product images being swapped out with form fields.
	 */
	public bindProductImageEvents() {
		const self = this;

		// Find any choices with [data-gspc-product-image] and immediately update the product image to selected choices.
		$('[data-gspc-product-image]').each(function () {
			// Ensure that the option or radio is selected
			if ($(this).is(':selected') || $(this).is(':checked')) {
				self.swapProductImageUsingChoice(this);
			}
		});

		// Add event listener to forms to listen for changes to choices with the data attribute.
		$('.gspc__form-wrapper').on(
			'change',
			'input[type="radio"]',
			function () {
				if ($(this).is('[data-gspc-product-image]')) {
					self.swapProductImageUsingChoice(this);
				}
			}
		);

		$('.gspc__form-wrapper').on('change', 'select', function () {
			const $selectedChoice = $(this).find(':selected');

			if ($selectedChoice.is('[data-gspc-product-image]')) {
				self.swapProductImageUsingChoice($selectedChoice);
			}
		});

		// Bind a listener to conditional logic to update the product image when a field is shown.
		window.gform.addAction(
			'gform_post_conditional_logic_field_action',
			(formId, action, targetId, defaultValues, isInit) => {
				if (parseInt(formId) !== this.formId) {
					return;
				}

				const $target = $(targetId);

				if (action === 'hide') {
					// Swap the image back to the original image.
					const $product = $target.closest('.product');

					this.swapProductImage(
						$product
							.find('.wp-post-image')
							.data('original-image') as string,
						$product
					);

					return;
				}

				const $productImageChoices = $target.find(
					'[data-gspc-product-image]'
				);

				if (!$productImageChoices.length) {
					return;
				}

				$productImageChoices.each(function (): void | false {
					// Ensure that the option or radio is selected
					if ($(this).is(':selected') || $(this).is(':checked')) {
						self.swapProductImageUsingChoice(this);
						return false;
					}
				});
			}
		);
	}

	/**
	 * Listen for WC Bookings updates so we can update the base WC product price in the form.
	 */
	public bindWCBookingsEvents() {
		// Update on initial load
		this.syncWCBookingsPrice();

		$(document).on(
			'wc_booking_form_changed',
			(event, form: JQuery<HTMLElement>) => {
				this.syncWCBookingsPrice();
			}
		);
	}

	/**
	 * Callback to update the base product price depending on the booking cost. This way, we can call it on initial load
	 * and inside the `wc_booking_form_changed` jQuery event.
	 */
	public syncWCBookingsPrice() {
		const $product = this.$formEl.closest('.product');

		const $price = $product.find(
			'.wc-bookings-booking-cost .woocommerce-Price-amount'
		);

		if (!$price.length) {
			return;
		}

		const currency = new window.Currency(
			window.gf_global.gf_currency_config
		);

		this.updateProductPrice(currency.toNumber($price.text()));
	}

	/**
	 * Bind callbacks on the form el for a variety of events such as the page changing.
	 */
	public bindFormEventsGPPT() {
		this.$formEl.on(
			'softValidationPageLoad.gppt',
			(event, newPage: number, oldPage: number, formId: number) => {
				const gppt = (window as any)?.[
					`GPPageTransitions_${this.formId}`
				];

				// Get the real index of the page as Page Transitions gives the visible page index.
				const realPageIndex = gppt?.getCurrentPageRealIndex() + 1;

				this.setCurrentPage(realPageIndex);
				this.updateUI();
			}
		);
	}

	/**
	 * Sets the current page properties.
	 *
	 * @param currentPage
	 */
	public setCurrentPage(currentPage: number) {
		this.currentPage = currentPage;
	}

	/**
	 * Get the count of the pages in the form.
	 */
	public getPageCount() {
		return this.$formEl.find('.gform_page').length ?? 1;
	}

	/**
	 * Sets the page state from GF 2.9's state API.
	 */
	public setPageState(pageState: PageState) {
		this.pageState = pageState;
	}

	/**
	 * Determine if we're on the last visible page.
	 */
	public isOnLastVisiblePage() {
		const { gfPageConditionalLogic: pageCl } = this;

		// If we have access to GP Page Transitions, utilize it.
		const gppt = (window as any)?.[`GPPageTransitions_${this.formId}`];

		if (gppt) {
			return gppt.isOnLastPage();
		}

		// If we have access to the page state in GF 2.9 and newer, utilize it.
		if (this.pageState) {
			return this.pageState.currentPage === this.pageState.lastPage;
		}

		// If Page Conditional Logic is used, it needs to be handled differently.
		if (pageCl) {
			// Loop through pages to figure out which is the last visible page.
			let lastVisiblePageIndex = 0;
			let pageI = 0;

			for (const page of pageCl.options.pages) {
				if (pageCl.isPageVisible(page)) {
					lastVisiblePageIndex = pageI;
				}

				pageI++;
			}

			return (this.currentPage ?? 1) - 1 === lastVisiblePageIndex + 1;
		}

		return (
			this.currentPage === this.$formEl.find('.gform_page').length ||
			(this.currentPage === 1 && this.getPageCount() === 0)
		);
	}

	/**
	 * Get the quantity div.
	 *
	 * For regular AJAX forms, we need to store a copy of the element during runtime as the AJAX refresh of the form
	 * can remove the element.
	 */
	get $quantityDiv() {
		const { quantityDivSelector: selector } = this;

		this.cacheQuantityDiv();

		if (this.$formEl?.find(selector).length) {
			return this.$formEl?.find(selector);
		}

		return this._$quantityDiv!;
	}

	/**
	 * Cache the Quantity div.
	 */
	public cacheQuantityDiv() {
		const { quantityDivSelector: selector } = this;

		if (!this._$quantityDiv || !this._$quantityDiv.length) {
			this._$quantityDiv = this.$formEl?.find(selector).clone();
		}
	}

	/**
	 * Cache the form attributes. Needed mostly for handling variations on AJAX forms.
	 */
	public cacheFormAttributes() {
		if (!this.formAttributes) {
			this.formAttributes = this.$formEl?.[0]?.attributes;
		}
	}

	/**
	 * Get the add to cart button
	 *
	 * For regular AJAX forms, we need to store a copy of the element during runtime as the AJAX refresh of the form
	 * can remove the element.
	 */
	get $addToCart() {
		const { addToCartSelector: selector } = this;

		this.cacheAddToCart();

		if (this.$formEl?.find(selector).length) {
			return this.$formEl?.find(selector);
		}

		return this._$addToCart!;
	}

	/**
	 * Cache the Add to Cart button.
	 */
	public cacheAddToCart() {
		if (!this._$addToCart || !this._$addToCart.length) {
			this._$addToCart = this.$formEl
				?.find(this.addToCartSelector)
				.clone();
		}
	}

	/**
	 * Get the variations selector.
	 *
	 * For regular AJAX forms, we need to store a copy of the element during runtime as the AJAX refresh of the form
	 * can remove the element.
	 */
	get $variations() {
		const { variationsSelector: selector } = this;

		this.cacheVariations();

		if (this.$formEl?.find(selector).length) {
			return this.$formEl?.find(selector);
		}

		return this._$variations!;
	}

	/**
	 * Cache variations selector so it can be re-shown when switching pages.
	 */
	public cacheVariations() {
		// Cache variation ID
		if (!this.variationId) {
			const variationIdValue = this.$formEl
				.find('input[name="variation_id"]')
				.val();

			if (variationIdValue) {
				this._variationId = parseInt(variationIdValue as string, 10);
			}
		}

		if (!this._$variations || !this._$variations.length) {
			this._$variations = this.$formEl.find(this.variationsSelector);
		}
	}

	/**
	 * Update UI based on the current page.
	 *
	 * Position (or hide) the Quantity and Add to Cart depending on the current page.
	 */
	public updateUI() {
		if (this.getPageCount() === 1 && !this.isAjax) {
			return;
		}

		// Ensure that the form attributes are cached. Sometimes pre_render is called too early if the form is in a modal.
		this.cacheFormAttributes();

		// Ensure that variation change event is bound. Again, may be an issue with modals.
		this.$formEl.off('found_variation', this.onVariationChange);
		this.$formEl.on('found_variation', this.onVariationChange);

		const {
			$currentPage,
			$formFooter,
			$quantityDiv,
			$addToCart,
			$variations,
		} = this;

		const $submitButton = this.$formEl.find(
			`#gform_submit_button_${this.formId}`
		);

		// Ensure "cart" class exists on the form element
		this.$formEl.addClass('cart');

		// Add missing attributes to form element.
		if (this.formAttributes) {
			// @ts-ignore - NamedNodeMap is iterable. https://developer.mozilla.org/en-US/docs/Web/API/Element/attributes
			for (const attr of this.formAttributes) {
				if (attr.name === 'action') {
					continue;
				}

				this.$formEl.attr(attr.name, attr.value);
			}
		}

		// If we have variations and the variations are missing, prepend to form.
		if (
			$variations.length &&
			this.$formEl.find(this.variationsSelector).length === 0
		) {
			$variations.prependTo(this.$formEl);

			$('.variations_form').each(function () {
				// @ts-ignore
				$(this).wc_variation_form();
			});
		}

		// Hide submit button again if it gets added
		$submitButton.addClass('gspc__submit--hidden');

		this.$formEl
			.find('.gspc--is-last-page')
			.removeClass('gspc--is-last-page');

		// Just in case the add to cart button is not the same as what was on the initial page, re-bind it.
		this.bindAddToCartClick();

		// If we're on the last visible page, move the buttons to the last visible page's footer.
		if (this.isOnLastVisiblePage()) {
			$quantityDiv.appendTo($formFooter).show();
			$addToCart.appendTo($formFooter).show();

			$currentPage.addClass('gspc--is-last-page');
		}

		// Add hidden input named "add-to-cart" with value from the Add to Cart button. Reason being, handleButtonClick
		// take over the form submission and the Add to Cart button's value won't go through.
		this.$formEl.find('input[type="hidden"][name="add-to-cart"]').remove();
		const $formFooterTarget = this.$formFooter.length
			? this.$formFooter
			: this.$formEl;
		$formFooterTarget.append(
			`<input type="hidden" name="add-to-cart" value="${this.productId}" class="gspc-add-to-cart-hidden">`
		);

		// Ensure that the variation_id hidden input exists if we have one stored. Could be lost from AJAX.
		if (this.variationId) {
			if (this.$formEl.find('input[name="variation_id"]').length === 0) {
				this.$formEl.append(
					`<input type="hidden" name="variation_id" value="${this.variationId}">`
				);
			}
		}
	}

	/**
	 * Binds the add to cart button click event.
	 */
	public bindAddToCartClick() {
		// Bind click on Add to Cart button to handle anti-spam techniques.
		this.$addToCart.off('click.gspcHandleButtonClick');
		this.$addToCart.on('click.gspcHandleButtonClick', function () {
			// @ts-ignore
			window.gform?.submission?.handleButtonClick(this);
		});
	}

	isValidMatchVariation = (
		isMatch: boolean,
		formId: number,
		rule: GFConditionalLogicRule
	) => {
		// Convert to string if rule's fieldId is stored as a number (possibly, Gravity Forms - Advanced Conditional Logic)
		if (typeof rule.fieldId === 'number') {
			rule.fieldId = '' + rule.fieldId;
		}
		if (rule.fieldId.indexOf('gspc_product_variation_') !== 0) {
			return isMatch;
		}

		const attributeName = rule.fieldId.replace(
			'gspc_product_variation_',
			''
		);
		const $variationField = this.$formEl.find(
			`select[data-attribute_name="attribute_${attributeName}"]`
		);

		return window.gf_matches_operation(
			$variationField.val(),
			rule.value,
			rule.operator
		);
	};

	getQuantityInput = () => {
		const $quantityInput = this.$formEl.find('input.qty');

		/**
		 * If the quantity field isn't found using the default selector,
		 * try looking for a field with .gspc__mapped-quantity.
		 */
		if (!$quantityInput.length) {
			// If the field is a single product, look for the quantity input inside of it.
			if (
				this.$formEl
					.find('.gspc__mapped-quantity')
					.hasClass('gfield--type-product')
			) {
				return this.$formEl.find(
					'.gspc__mapped-quantity input[id^="ginput_quantity_"]'
				);
			}

			// For any other field type, just look for the first input.
			return this.$formEl.find('.gspc__mapped-quantity input').first();
		}

		return $quantityInput;
	};

	isValidMatchQuantity = (
		isMatch: boolean,
		formId: number,
		rule: GFConditionalLogicRule
	) => {
		if (rule.fieldId !== 'gspc_quantity') {
			return isMatch;
		}

		return window.gf_matches_operation(
			this.getQuantityInput().val(),
			rule.value,
			rule.operator
		);
	};
}

window.GSProductConfigurator = GSProductConfigurator;
